/**
 * Import Service — Shared internal helpers
 *
 * Non-exported (module-internal) helpers and type aliases shared by the
 * per-domain import service files (sources / import-sync). Moved verbatim out
 * of the original import.service.ts during the per-domain split. These are NOT
 * part of the public service surface — the barrel does not re-export them.
 */

import { ImportSource as SharedImportSource } from '../types'

// Type alias for local use - ImportSource comes from ../types
export type ImportSource = SharedImportSource

// Local alias for removed types
export type ImportSourceResponse = Record<string, unknown>

/**
 * Transform backend ImportSourceResponse to frontend ImportSource format
 */
// Backend status values (see backend mapConnectionStatusToImportSourceStatus).
// Passed through honestly — 'inactive' and 'pending' are real states the UI
// must render, not values to be rewritten into 'active'.
// 'paused' is as real as the rest: the backend derives it from a `platform_jobs`
// row in state 'paused'. Dropping it here made a paused source arrive as
// 'inactive' — indistinguishable from a disconnected one, and with no row action.
const VALID_SOURCE_STATUSES = [
  'active',
  'inactive',
  'pending',
  'error',
  'syncing',
  'paused',
] as const

function toImportSourceStatus(rawStatus: unknown): ImportSource['status'] {
  const candidate = String(rawStatus ?? '')
  return (VALID_SOURCE_STATUSES as readonly string[]).includes(candidate)
    ? (candidate as ImportSource['status'])
    : 'inactive'
}

export function transformToImportSource(source: Record<string, unknown>): ImportSource {
  // The UI's "Última sincronización" column must show WHEN the last sync ran.
  // The backend sends two distinct timestamps:
  //   - `lastSync`     → completedAt of the newest completed job (actual sync
  //                      execution time — what the user expects to see)
  //   - `lastSyncedAt` → incremental-sync cursor (max imported row date), which
  //                      can lag days/months behind the actual sync run
  // Prefer `lastSync` and only fall back to the cursor when it is absent. We
  // deliberately do NOT fall back to `lastConnectedAt` — that field tracks
  // credential validity, not data freshness.
  const rawLastSyncTime = source.lastSync
    ? String(source.lastSync)
    : source.lastSyncedAt
      ? String(source.lastSyncedAt)
      : null

  // Raw incremental-sync cursor, kept separate from the display timestamp.
  const rawLastSyncedAtCursor = source.lastSyncedAt ? String(source.lastSyncedAt) : null

  // The backend serializes `import_from_date` as an ISO-8601 string (or null).
  // We pass it through as a string so the EditSourceModal can extract the
  // `YYYY-MM-DD` portion without timezone-conversion drift, and the rest of the
  // UI can format it as needed.
  const rawImportFromDate =
    source.importFromDate !== undefined && source.importFromDate !== null
      ? String(source.importFromDate)
      : null

  // Last-sync error fields populated by the backend when a sync job fails
  // (see backend task #4). Both are nullable when no error has occurred.
  const rawLastError =
    source.lastError !== undefined && source.lastError !== null ? String(source.lastError) : null
  const rawLastErrorAt =
    source.lastErrorAt !== undefined && source.lastErrorAt !== null
      ? String(source.lastErrorAt)
      : null

  return {
    id: String(source.id ?? ''), // Use UUID directly as string ID
    uuid: String(source.id ?? ''), // Store original UUID for API calls (delete, update, sync)
    name: String(source.name ?? ''),
    type: source.type as 'csv' | 'api' | 'blockchain',
    status: toImportSourceStatus(source.status),
    lastSync: rawLastSyncTime ? new Date(rawLastSyncTime) : null,
    lastSyncedAt: rawLastSyncedAtCursor,
    config: (source.config as Record<string, unknown>) ?? undefined,
    metadata: source.tags ? { tags: source.tags as string[] } : undefined,
    createdAt: new Date(String(source.createdAt ?? Date.now())),
    updatedAt: new Date(String(source.updatedAt ?? Date.now())),
    importFromDate: rawImportFromDate,
    lastError: rawLastError,
    lastErrorAt: rawLastErrorAt,
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- '' source/provider must fall through to the next candidate
    source: String(source.source || source.provider || source.name), // Use source field or fallback to provider/name
    transactions: (source.transactions as number) || 0, // Use backend transaction count
    // Grouping key for `transactions` — see ImportSource.transactionCountKey.
    // Must be carried across: this is an explicit mapper, so a field it does
    // not name is silently dropped and the disclosure falls back to summing.
    transactionCountKey:
      typeof source.transactionCountKey === 'string' ? source.transactionCountKey : undefined,
    // El nombre del catálogo que resuelve el servidor. Mismo aviso que arriba:
    // este mapeador es EXPLÍCITO, así que un campo que no nombre se pierde y la
    // columna «Medio» vuelve a re-deletrear el slug.
    sourceDisplayName:
      typeof source.sourceDisplayName === 'string' ? source.sourceDisplayName : undefined,
    tags: (source.tags as string[]) || [], // Default empty tags array
  }
}
